import { useState, type ReactElement } from 'react';
import {
  BOARD_BACKGROUND_UPLOAD_CONTENT_TYPES,
  BOARD_BACKGROUND_UPLOAD_MAX_BYTES,
  type BoardBackground,
  type BoardBackgroundCustomApplyBody,
  type BoardBackgroundPut,
  type BoardBackgroundUploadConfirmBody,
  type CustomBoardBackground,
} from '@molt/shared';
import {
  BOARD_BACKGROUND_SWATCHES,
  boardBackgroundLabel,
  boardBackgroundPhotoAttribution,
  boardBackgroundPresetLabel,
  customBoardBackgroundDownloadUrl,
  colourBackgroundPut,
  filterBoardBackgroundPhotos,
  photoBackgroundPut,
} from '@/lib/board-backgrounds';
import { Button } from '@/components/Button';
import { Notice } from '@/components/Notice';
import { TextField } from '@/components/TextField';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * FE-80.1 - the four whole-summary keys, resolved by the render.
 *
 * The source composed this line from three nodes (`{mode} -{' '}{treatment}`);
 * a two-key join would fix the separator and the word order for every locale.
 * A module constant cannot call `t`, so this holds the KEY and the render
 * resolves it.
 */
function customSummaryKey(
  renderMode: CustomBoardBackground['renderMode'],
  textTreatment: CustomBoardBackground['textTreatment'],
): string {
  if (renderMode === 'tiled') {
    return textTreatment === 'light'
      ? 'boardAdmin.background.customSummary.tiledLight'
      : 'boardAdmin.background.customSummary.tiledDark';
  }
  return textTreatment === 'light'
    ? 'boardAdmin.background.customSummary.fullCoverLight'
    : 'boardAdmin.background.customSummary.fullCoverDark';
}

export interface BoardBackgroundPickerProps {
  boardId: string;
  background: BoardBackground;
  canChange: boolean;
  disabledReason: string | null;
  onChange: (background: BoardBackgroundPut) => void;
  customBackgrounds?: readonly CustomBoardBackground[] | undefined;
  customBackgroundsLoading?: boolean | undefined;
  onUploadCustomBackground?:
    | ((input: {
        readonly file: File;
        readonly renderMode: BoardBackgroundUploadConfirmBody['renderMode'];
        readonly textTreatment: BoardBackgroundUploadConfirmBody['textTreatment'];
        readonly applyToBoard: boolean;
      }) => void)
    | undefined;
  onApplyCustomBackground?:
    ((backgroundId: string, body: BoardBackgroundCustomApplyBody) => void) | undefined;
  busy?: boolean;
  error?: string | undefined;
}

export function BoardBackgroundPicker({
  boardId,
  background,
  canChange,
  disabledReason,
  onChange,
  customBackgrounds = [],
  customBackgroundsLoading = false,
  onUploadCustomBackground,
  onApplyCustomBackground,
  busy = false,
  error,
}: BoardBackgroundPickerProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [photoQuery, setPhotoQuery] = useState('');
  const [customFile, setCustomFile] = useState<File | null>(null);
  const [customRenderMode, setCustomRenderMode] =
    useState<BoardBackgroundUploadConfirmBody['renderMode']>('full_cover');
  const [customTextTreatment, setCustomTextTreatment] =
    useState<BoardBackgroundUploadConfirmBody['textTreatment']>('dark');
  const [localError, setLocalError] = useState<string | null>(null);
  const photos = filterBoardBackgroundPhotos(t, photoQuery);
  const currentLabel = boardBackgroundLabel(t, background);

  function withTreatment(textTreatment: BoardBackgroundPut['textTreatment']) {
    if (background.type === 'colour' && background.colour !== null) {
      onChange({ ...colourBackgroundPut(background.colour), textTreatment });
      return;
    }
    if (background.type === 'photo' && background.photoRef !== null) {
      onChange({ ...photoBackgroundPut(background.photoRef), textTreatment });
      return;
    }
    if (background.type === 'custom_upload' && background.backgroundCustomId !== null) {
      onApplyCustomBackground?.(background.backgroundCustomId, {
        renderMode: background.renderMode,
        textTreatment,
      });
    }
  }

  function withRenderMode(renderMode: BoardBackgroundCustomApplyBody['renderMode']) {
    if (background.type !== 'custom_upload' || background.backgroundCustomId === null) {
      return;
    }
    onApplyCustomBackground?.(background.backgroundCustomId, {
      renderMode,
      textTreatment: background.textTreatment,
    });
  }

  function chooseFile(file: File | null) {
    setLocalError(null);
    if (file === null) {
      setCustomFile(null);
      return;
    }
    if (
      !BOARD_BACKGROUND_UPLOAD_CONTENT_TYPES.includes(
        file.type as (typeof BOARD_BACKGROUND_UPLOAD_CONTENT_TYPES)[number],
      )
    ) {
      setCustomFile(null);
      setLocalError(t('boardAdmin.background.error.contentType'));
      return;
    }
    if (file.size > BOARD_BACKGROUND_UPLOAD_MAX_BYTES) {
      setCustomFile(null);
      setLocalError(t('boardAdmin.background.error.tooLarge'));
      return;
    }
    setCustomFile(file);
  }

  function uploadCustomBackground() {
    if (customFile === null) {
      setLocalError(t('boardAdmin.background.error.noFile'));
      return;
    }
    setLocalError(null);
    onUploadCustomBackground?.({
      file: customFile,
      renderMode: customRenderMode,
      textTreatment: customTextTreatment,
      applyToBoard: true,
    });
  }

  const disabled = !canChange || busy;
  const reasonProps = !canChange && disabledReason !== null ? { disabledReason } : {};

  return (
    <div className="flex flex-col gap-4">
      {error ? <Notice tone="bad" body={error} /> : null}
      {localError ? <Notice tone="bad" body={localError} /> : null}
      {!canChange && disabledReason !== null ? (
        <Notice tone="locked" body={disabledReason} />
      ) : null}

      <div className="rounded-card bg-surface-raised p-3 text-sm text-ink">
        {t('boardAdmin.background.current')}{' '}
        <span className="font-medium">{currentLabel}</span>
      </div>

      <div
        role="group"
        aria-label={t('boardAdmin.background.textGroup')}
        className="flex gap-2"
      >
        <Button
          variant="toggle"
          size="sm"
          pressed={background.textTreatment === 'light'}
          className="h-auto min-h-7 flex-1 py-1 leading-tight"
          disabled={disabled || !canChange}
          {...reasonProps}
          onClick={() => withTreatment('light')}
        >
          {t('boardAdmin.background.text.light')}
        </Button>
        <Button
          variant="toggle"
          size="sm"
          pressed={background.textTreatment === 'dark'}
          className="h-auto min-h-7 flex-1 py-1 leading-tight"
          disabled={disabled || !canChange}
          {...reasonProps}
          onClick={() => withTreatment('dark')}
        >
          {t('boardAdmin.background.text.dark')}
        </Button>
      </div>

      <div
        role="group"
        aria-label={t('boardAdmin.background.colours')}
        className="flex flex-col gap-2"
      >
        <h3 className="text-sm font-semibold text-ink">
          {t('boardAdmin.background.colours')}
        </h3>
        <div className="grid grid-cols-4 gap-2">
          {BOARD_BACKGROUND_SWATCHES.map((swatch) => (
            <Button
              key={swatch.id}
              variant="quiet"
              className="h-12 overflow-hidden border border-border-strong p-0 aria-pressed:ring-2 aria-pressed:ring-accent aria-pressed:ring-offset-2 aria-pressed:ring-offset-surface hover:brightness-95"
              style={swatch.style}
              aria-label={t('boardAdmin.background.swatch', {
                name: boardBackgroundPresetLabel(t, swatch),
              })}
              aria-pressed={
                background.type === 'colour' && background.colour === swatch.id
              }
              disabled={disabled}
              {...reasonProps}
              onClick={() => onChange(colourBackgroundPut(swatch.id))}
            />
          ))}
        </div>
      </div>

      {background.type === 'custom_upload' && background.backgroundCustomId !== null ? (
        <div
          role="group"
          aria-label={t('boardAdmin.background.customOptions')}
          className="flex flex-col gap-2"
        >
          <h3 className="text-sm font-semibold text-ink">
            {t('boardAdmin.background.customOptions')}
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="toggle"
              size="sm"
              pressed={background.renderMode === 'full_cover'}
              disabled={disabled}
              {...reasonProps}
              onClick={() => withRenderMode('full_cover')}
            >
              {t('boardAdmin.background.renderMode.fullCover')}
            </Button>
            <Button
              variant="toggle"
              size="sm"
              pressed={background.renderMode === 'tiled'}
              disabled={disabled}
              {...reasonProps}
              onClick={() => withRenderMode('tiled')}
            >
              {t('boardAdmin.background.renderMode.tiled')}
            </Button>
          </div>
        </div>
      ) : null}

      <TextField
        label={t('boardAdmin.background.photoSearch')}
        type="search"
        value={photoQuery}
        onChange={(event) => setPhotoQuery(event.currentTarget.value)}
      />

      <div
        role="group"
        aria-label={t('boardAdmin.background.photos')}
        className="flex flex-col gap-2"
      >
        <h3 className="text-sm font-semibold text-ink">
          {t('boardAdmin.background.photos')}
        </h3>
        <div className="grid grid-cols-1 gap-2">
          {photos.map((photo) => (
            <Button
              key={photo.photoRef}
              variant="quiet"
              className="h-20 justify-start overflow-hidden border border-border-strong p-0 text-left"
              // The thumbnail row is laid out on `Button`'s content span, which
              // otherwise sizes to its content: `h-full` collapsed to nothing,
              // the text column dropped below, and `truncate` never engaged.
              contentClassName="flex h-full min-w-0 flex-1 items-center"
              aria-label={t('boardAdmin.background.photo', {
                name: boardBackgroundPresetLabel(t, photo),
              })}
              disabled={disabled}
              {...reasonProps}
              onClick={() => onChange(photoBackgroundPut(photo.photoRef))}
            >
              <span
                aria-hidden="true"
                className="block h-full w-24 shrink-0"
                style={photo.style}
              />
              <span className="flex min-w-0 flex-col px-2">
                <span className="truncate text-sm font-medium">
                  {boardBackgroundPresetLabel(t, photo)}
                </span>
                <span className="truncate text-xs text-ink-muted">
                  {boardBackgroundPhotoAttribution(t, photo)}
                </span>
              </span>
            </Button>
          ))}
        </div>
      </div>

      <div
        role="group"
        aria-label={t('boardAdmin.background.customs')}
        className="flex flex-col gap-2"
      >
        <h3 className="text-sm font-semibold text-ink">
          {t('boardAdmin.background.customs')}
        </h3>
        <p className="text-sm text-ink-muted">{t('boardAdmin.background.customsNote')}</p>
        <label className="flex flex-col gap-1 text-sm text-ink">
          <span className="font-medium">{t('boardAdmin.background.uploadLabel')}</span>
          <input
            type="file"
            accept={BOARD_BACKGROUND_UPLOAD_CONTENT_TYPES.join(',')}
            disabled={disabled}
            className="text-sm file:mr-2 file:rounded-card file:border file:border-border-strong file:bg-surface-raised file:px-2 file:py-1 file:text-ink"
            onChange={(event) => chooseFile(event.currentTarget.files?.[0] ?? null)}
          />
        </label>
        <div
          role="group"
          aria-label={t('boardAdmin.background.uploadRenderMode')}
          className="grid grid-cols-2 gap-2"
        >
          <Button
            variant="toggle"
            size="sm"
            pressed={customRenderMode === 'full_cover'}
            disabled={disabled}
            {...reasonProps}
            onClick={() => setCustomRenderMode('full_cover')}
          >
            {t('boardAdmin.background.upload.fullCover')}
          </Button>
          <Button
            variant="toggle"
            size="sm"
            pressed={customRenderMode === 'tiled'}
            disabled={disabled}
            {...reasonProps}
            onClick={() => setCustomRenderMode('tiled')}
          >
            {t('boardAdmin.background.upload.tiled')}
          </Button>
        </div>
        <div
          role="group"
          aria-label={t('boardAdmin.background.uploadTextGroup')}
          className="grid grid-cols-2 gap-2"
        >
          <Button
            variant="toggle"
            size="sm"
            pressed={customTextTreatment === 'light'}
            disabled={disabled}
            {...reasonProps}
            onClick={() => setCustomTextTreatment('light')}
          >
            {t('boardAdmin.background.upload.lightText')}
          </Button>
          <Button
            variant="toggle"
            size="sm"
            pressed={customTextTreatment === 'dark'}
            disabled={disabled}
            {...reasonProps}
            onClick={() => setCustomTextTreatment('dark')}
          >
            {t('boardAdmin.background.upload.darkText')}
          </Button>
        </div>
        <Button
          variant="primary"
          size="sm"
          disabled={
            disabled || customFile === null || onUploadCustomBackground === undefined
          }
          {...reasonProps}
          onClick={uploadCustomBackground}
        >
          {t('boardAdmin.background.uploadSubmit')}
        </Button>

        <div className="flex flex-col gap-2">
          {customBackgroundsLoading ? (
            <p className="text-sm text-ink-muted">
              {t('boardAdmin.background.customsLoading')}
            </p>
          ) : customBackgrounds.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {t('boardAdmin.background.customsEmpty')}
            </p>
          ) : (
            customBackgrounds.map((custom) => (
              <Button
                key={custom.id}
                variant="quiet"
                className="h-20 justify-start overflow-hidden border border-border-strong p-0 text-left"
                contentClassName="flex h-full min-w-0 flex-1 items-center"
                aria-label={t('boardAdmin.background.customLabel', {
                  date: new Date(custom.createdAt).toLocaleDateString(),
                })}
                disabled={disabled}
                {...reasonProps}
                onClick={() =>
                  onApplyCustomBackground?.(custom.id, {
                    renderMode: custom.renderMode,
                    textTreatment: custom.textTreatment,
                  })
                }
              >
                <span
                  aria-hidden="true"
                  className="block h-full w-24 shrink-0 bg-board"
                  style={{
                    backgroundImage: `url(${customBoardBackgroundDownloadUrl(boardId, custom.id)})`,
                    backgroundPosition: 'center',
                    backgroundSize: custom.renderMode === 'tiled' ? 'auto' : 'cover',
                    backgroundRepeat:
                      custom.renderMode === 'tiled' ? 'repeat' : 'no-repeat',
                  }}
                />
                <span className="flex min-w-0 flex-col px-2">
                  <span className="truncate text-sm font-medium">
                    {t('boardAdmin.background.customUploaded', {
                      date: new Date(custom.createdAt).toLocaleDateString(),
                    })}
                  </span>
                  <span className="truncate text-xs text-ink-muted">
                    {t(customSummaryKey(custom.renderMode, custom.textTreatment))}
                  </span>
                </span>
              </Button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
